import React, { useEffect, useState, useCallback } from 'react';
import { Layout } from '../components/Layout';
import api from '../lib/api';
import { toast } from 'sonner';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Card } from '../components/ui/card';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '../components/ui/tabs';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { Checkbox } from '../components/ui/checkbox';
import { Download, Mail, Save, ShieldCheck, FileSpreadsheet, Loader2, FilePlus, History, CheckCircle2, XCircle, Search } from 'lucide-react';

const fmtDateTime = (iso) => {
  if (!iso) return '—';
  try { return new Date(iso).toLocaleString('en-GB', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' }); }
  catch { return iso; }
};

const TABLES = [
  { key: 'deposit', label: 'Deposit Protection' },
  { key: 'ibg', label: 'Insurance Backed Guarantee (IBG)' },
];

const HiesTable = ({ table, onSent }) => {
  const [columns, setColumns] = useState([]);
  const [records, setRecords] = useState([]);
  const [loading, setLoading] = useState(true);
  const [edits, setEdits] = useState({}); // { customer_id: { key: value } }
  const [savingId, setSavingId] = useState(null);
  const [sending, setSending] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [colsRes, recRes] = await Promise.all([
        api.get('/hies/columns'),
        api.get(`/hies/records?table=${table}`),
      ]);
      setColumns(colsRes.data.columns || []);
      setRecords(recRes.data.records || []);
      setEdits({});
    } catch {
      toast.error('Failed to load HIES records');
    } finally {
      setLoading(false);
    }
  }, [table]);

  useEffect(() => { load(); }, [load]);

  const cellValue = (rec, key) => {
    const e = edits[rec.customer_id];
    if (e && key in e) return e[key];
    return rec[key] ?? '';
  };

  const onEdit = (cid, key, value) => {
    setEdits((prev) => ({ ...prev, [cid]: { ...(prev[cid] || {}), [key]: value } }));
  };

  const saveRow = async (cid) => {
    if (!edits[cid] || Object.keys(edits[cid]).length === 0) return;
    setSavingId(cid);
    try {
      await api.patch(`/hies/records/${cid}`, { fields: edits[cid] });
      toast.success('Saved');
      setRecords((prev) => prev.map((r) => (r.customer_id === cid ? { ...r, ...edits[cid] } : r)));
      setEdits((prev) => { const n = { ...prev }; delete n[cid]; return n; });
    } catch {
      toast.error('Save failed');
    } finally {
      setSavingId(null);
    }
  };

  const exportCsv = async () => {
    try {
      const resp = await api.get(`/hies/export.csv?table=${table}`, { responseType: 'blob' });
      const url = URL.createObjectURL(new Blob([resp.data], { type: 'text/csv' }));
      const a = document.createElement('a');
      a.href = url;
      a.download = `HIES-${table === 'deposit' ? 'Deposit-Protection' : 'IBG'}.csv`;
      a.click();
      setTimeout(() => URL.revokeObjectURL(url), 5000);
    } catch {
      toast.error('Export failed');
    }
  };

  const sendNow = async () => {
    setSending(true);
    try {
      await api.post(`/hies/send-email?table=${table}`);
      toast.success('HIES email sent to configured recipients');
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Email send failed');
    } finally {
      setSending(false);
      onSent?.();
    }
  };

  return (
    <div className="space-y-4" data-testid={`hies-table-${table}`}>
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <p className="text-sm text-slate-500" data-testid={`hies-count-${table}`}>
          {loading ? 'Loading…' : `${records.length} record(s)`}
        </p>
        <div className="flex gap-2">
          <Button variant="outline" onClick={exportCsv} data-testid={`hies-export-${table}`}>
            <Download className="w-4 h-4 mr-2" /> Export CSV
          </Button>
          <Button onClick={sendNow} disabled={sending} data-testid={`hies-send-${table}`}>
            {sending ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Mail className="w-4 h-4 mr-2" />}
            Send email now
          </Button>
        </div>
      </div>

      <div className="overflow-x-auto border border-slate-200 rounded-lg">
        <table className="w-full text-xs">
          <thead className="bg-slate-50 sticky top-0">
            <tr>
              {columns.map((c) => (
                <th key={c.key} className="px-2 py-2 text-left font-semibold text-slate-600 whitespace-nowrap border-b border-slate-200">
                  {c.label}
                  {c.editable && <span className="ml-1 text-amber-500" title="Editable">✎</span>}
                </th>
              ))}
              <th className="px-2 py-2 border-b border-slate-200"></th>
            </tr>
          </thead>
          <tbody>
            {!loading && records.length === 0 && (
              <tr><td colSpan={columns.length + 1} className="px-3 py-8 text-center text-slate-400">No jobs in this stage yet.</td></tr>
            )}
            {records.map((rec) => {
              const dirty = edits[rec.customer_id] && Object.keys(edits[rec.customer_id]).length > 0;
              return (
                <tr key={rec.customer_id} className="border-b border-slate-100 hover:bg-slate-50" data-testid={`hies-row-${rec.customer_id}`}>
                  {columns.map((c) => (
                    <td key={c.key} className="px-2 py-1 whitespace-nowrap">
                      {c.editable ? (
                        <Input
                          value={cellValue(rec, c.key)}
                          onChange={(e) => onEdit(rec.customer_id, c.key, e.target.value)}
                          className="h-7 text-xs min-w-[110px]"
                          data-testid={`hies-cell-${rec.customer_id}-${c.key}`}
                        />
                      ) : (
                        <span className="text-slate-700">{String(cellValue(rec, c.key))}</span>
                      )}
                    </td>
                  ))}
                  <td className="px-2 py-1">
                    {dirty && (
                      <Button size="sm" variant="ghost" onClick={() => saveRow(rec.customer_id)} disabled={savingId === rec.customer_id} data-testid={`hies-save-${rec.customer_id}`}>
                        {savingId === rec.customer_id ? <Loader2 className="w-3 h-3 animate-spin" /> : <Save className="w-3 h-3" />}
                      </Button>
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
};

const HiesSettings = () => {
  const [settings, setSettings] = useState({ member_number: '', scheme: '', recipients: [] });
  const [recipientsText, setRecipientsText] = useState('');
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    api.get('/hies/settings').then((r) => {
      setSettings(r.data);
      setRecipientsText((r.data.recipients || []).join(', '));
    }).catch(() => toast.error('Failed to load settings'));
  }, []);

  const save = async () => {
    setSaving(true);
    try {
      const recipients = recipientsText.split(',').map((s) => s.trim()).filter(Boolean);
      const r = await api.put('/hies/settings', { ...settings, recipients });
      setSettings(r.data);
      setRecipientsText((r.data.recipients || []).join(', '));
      toast.success('HIES settings saved');
    } catch {
      toast.error('Save failed');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Card className="p-6 max-w-xl space-y-4" data-testid="hies-settings">
      <div>
        <label className="text-sm font-medium text-slate-700">Member Number</label>
        <Input value={settings.member_number} onChange={(e) => setSettings({ ...settings, member_number: e.target.value })} data-testid="hies-member-number" />
      </div>
      <div>
        <label className="text-sm font-medium text-slate-700">Scheme</label>
        <Input value={settings.scheme} onChange={(e) => setSettings({ ...settings, scheme: e.target.value })} data-testid="hies-scheme" />
      </div>
      <div>
        <label className="text-sm font-medium text-slate-700">Month-end email recipients</label>
        <Input value={recipientsText} onChange={(e) => setRecipientsText(e.target.value)} placeholder="comma-separated emails" data-testid="hies-recipients" />
        <p className="text-xs text-slate-400 mt-1">Comma-separated. These receive the automatic month-end export and any "Send email now" exports.</p>
      </div>
      <Button onClick={save} disabled={saving} data-testid="hies-settings-save">
        {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />} Save settings
      </Button>
    </Card>
  );
};

const monthStart = () => { const d = new Date(); return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-01`; };
const today = () => new Date().toISOString().slice(0, 10);

const CustomReportDialog = ({ open, onOpenChange, onSent }) => {
  const [search, setSearch] = useState('');
  const [statusGroup, setStatusGroup] = useState('all');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [customers, setCustomers] = useState([]);
  const [selected, setSelected] = useState({}); // { id: customer }
  const [loading, setLoading] = useState(false);
  const [downloading, setDownloading] = useState(false);
  const [emailing, setEmailing] = useState(false);

  const runSearch = useCallback(async (term, group, from, to) => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ search: term || '', status_group: group || 'all', limit: '300' });
      if (from) params.set('date_from', from);
      if (to) params.set('date_to', to);
      const r = await api.get(`/hies/customers?${params.toString()}`);
      setCustomers(r.data.customers || []);
    } catch {
      toast.error('Failed to load customers');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (!open) return;
    const t = setTimeout(() => runSearch(search, statusGroup, dateFrom, dateTo), 350);
    return () => clearTimeout(t);
  }, [search, statusGroup, dateFrom, dateTo, open, runSearch]);

  const applyPreset = (preset) => {
    if (preset === 'all') { setStatusGroup('all'); setDateFrom(''); setDateTo(''); }
    else if (preset === 'deposit') { setStatusGroup('deposit'); setDateFrom(''); setDateTo(''); }
    else if (preset === 'ibg') { setStatusGroup('ibg'); setDateFrom(''); setDateTo(''); }
    else if (preset === 'completed-month') { setStatusGroup('completed'); setDateFrom(monthStart()); setDateTo(today()); }
  };

  const toggle = (c) => {
    setSelected((prev) => {
      const n = { ...prev };
      if (n[c.id]) delete n[c.id]; else n[c.id] = c;
      return n;
    });
  };

  const allShownSelected = customers.length > 0 && customers.every((c) => selected[c.id]);
  const toggleSelectAll = () => {
    setSelected((prev) => {
      const n = { ...prev };
      if (allShownSelected) { customers.forEach((c) => delete n[c.id]); }
      else { customers.forEach((c) => { n[c.id] = c; }); }
      return n;
    });
  };

  const selectedList = Object.values(selected);

  const presets = [
    { key: 'all', label: 'All' },
    { key: 'deposit', label: 'Deposit stage' },
    { key: 'ibg', label: 'IBG stage' },
    { key: 'completed-month', label: 'Completed this month' },
  ];
  const activePreset = (() => {
    if (statusGroup === 'completed' && dateFrom === monthStart() && dateTo === today()) return 'completed-month';
    if (!dateFrom && !dateTo) return statusGroup === 'all' ? 'all' : statusGroup;
    return null;
  })();

  const download = async () => {
    if (selectedList.length === 0) return;
    setDownloading(true);
    try {
      const resp = await api.post('/hies/custom-export.csv',
        { customer_ids: selectedList.map((c) => c.id) },
        { responseType: 'blob' });
      const url = URL.createObjectURL(new Blob([resp.data], { type: 'text/csv' }));
      const a = document.createElement('a');
      a.href = url;
      a.download = 'HIES-Custom-Report.csv';
      a.click();
      setTimeout(() => URL.revokeObjectURL(url), 5000);
      toast.success(`Report generated for ${selectedList.length} customer(s)`);
    } catch {
      toast.error('Failed to generate report');
    } finally {
      setDownloading(false);
    }
  };

  const emailReport = async () => {
    if (selectedList.length === 0) return;
    setEmailing(true);
    try {
      const r = await api.post('/hies/custom-email', { customer_ids: selectedList.map((c) => c.id) });
      toast.success(`Report emailed to ${(r.data.recipients || []).length} recipient(s)`);
      onSent?.();
      onOpenChange(false);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to email report');
    } finally {
      setEmailing(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl" data-testid="hies-custom-report-dialog">
        <DialogHeader>
          <DialogTitle>Create a custom HIES report</DialogTitle>
        </DialogHeader>
        <div className="space-y-3">
          <div className="relative">
            <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
            <Input
              placeholder="Search by name, postcode, address or email…"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="pl-9"
              data-testid="hies-report-search"
            />
          </div>

          <div className="flex flex-wrap gap-2" data-testid="hies-report-presets">
            {presets.map((p) => (
              <button
                key={p.key}
                type="button"
                onClick={() => applyPreset(p.key)}
                className={`px-2.5 py-1 rounded-full text-xs font-medium border transition-colors ${activePreset === p.key ? 'bg-emerald-600 text-white border-emerald-600' : 'bg-white text-slate-600 border-slate-300 hover:border-slate-400'}`}
                data-testid={`hies-report-preset-${p.key}`}
              >
                {p.label}
              </button>
            ))}
          </div>

          <div className="flex items-center gap-2 text-xs text-slate-500">
            <span>Date range:</span>
            <Input type="date" value={dateFrom} onChange={(e) => setDateFrom(e.target.value)} className="h-8 w-auto" data-testid="hies-report-date-from" />
            <span>to</span>
            <Input type="date" value={dateTo} onChange={(e) => setDateTo(e.target.value)} className="h-8 w-auto" data-testid="hies-report-date-to" />
            <span className="text-slate-400">(uses completion / contract-sign date)</span>
          </div>

          <div className="flex items-center justify-between">
            <label className="flex items-center gap-2 text-xs font-medium text-slate-600 cursor-pointer">
              <Checkbox checked={allShownSelected} onCheckedChange={toggleSelectAll} data-testid="hies-report-select-all" />
              Select all shown ({customers.length})
            </label>
            <p className="text-xs text-slate-500" data-testid="hies-report-selected-count">{selectedList.length} selected</p>
          </div>

          <div className="border border-slate-200 rounded-lg max-h-72 overflow-y-auto divide-y divide-slate-100">
            {loading && <div className="p-4 text-center text-slate-400 text-sm">Loading…</div>}
            {!loading && customers.length === 0 && <div className="p-4 text-center text-slate-400 text-sm">No customers found.</div>}
            {!loading && customers.map((c) => (
              <label key={c.id} className="flex items-center gap-3 px-3 py-2 hover:bg-slate-50 cursor-pointer" data-testid={`hies-report-customer-${c.id}`}>
                <Checkbox checked={!!selected[c.id]} onCheckedChange={() => toggle(c)} data-testid={`hies-report-check-${c.id}`} />
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium text-slate-800 truncate">{c.first_name} {c.last_name}</p>
                  <p className="text-xs text-slate-500 truncate">{c.address || ''} {c.postcode ? `· ${c.postcode}` : ''} · {c.status}{c.key_date ? ` · ${c.key_date}` : ''}</p>
                </div>
              </label>
            ))}
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button variant="outline" onClick={emailReport} disabled={emailing || selectedList.length === 0} data-testid="hies-report-email">
            {emailing ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Mail className="w-4 h-4 mr-2" />}
            Email this report
          </Button>
          <Button onClick={download} disabled={downloading || selectedList.length === 0} data-testid="hies-report-download">
            {downloading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Download className="w-4 h-4 mr-2" />}
            Generate CSV ({selectedList.length})
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

const SendHistory = ({ refreshKey }) => {
  const [data, setData] = useState({ last_sent: null, log: [] });

  useEffect(() => {
    api.get('/hies/send-log').then((r) => setData(r.data)).catch(() => {});
  }, [refreshKey]);

  return (
    <Card className="p-4" data-testid="hies-send-history">
      <div className="flex items-center gap-2 mb-3">
        <History className="w-4 h-4 text-slate-500" />
        <h3 className="text-sm font-semibold text-slate-700">Submission history</h3>
        <span className="ml-auto text-xs text-slate-500" data-testid="hies-last-sent">
          Last sent: <strong>{fmtDateTime(data.last_sent)}</strong>
        </span>
      </div>
      {data.log.length === 0 ? (
        <p className="text-xs text-slate-400">No submissions sent yet.</p>
      ) : (
        <div className="space-y-1.5 max-h-48 overflow-y-auto">
          {data.log.map((l, i) => (
            <div key={i} className="flex items-center gap-2 text-xs text-slate-600" data-testid={`hies-log-row-${i}`}>
              {l.success ? <CheckCircle2 className="w-3.5 h-3.5 text-emerald-500" /> : <XCircle className="w-3.5 h-3.5 text-red-500" />}
              <span className="text-slate-800 font-medium">{fmtDateTime(l.sent_at)}</span>
              <span className="px-1.5 py-0.5 rounded bg-slate-100 text-slate-500">{l.trigger}</span>
              <span>{(l.tables || []).map((t) => `${t === 'deposit' ? 'Deposit' : t === 'ibg' ? 'IBG' : 'Custom'} (${(l.counts || {})[t] ?? 0})`).join(', ')}</span>
              <span className="text-slate-400 ml-auto truncate">{(l.recipients || []).length} recipient(s){l.sharepoint_saved ? ` · ${l.sharepoint_saved} to SharePoint` : ''}</span>
            </div>
          ))}
        </div>
      )}
    </Card>
  );
};

const AdminHies = () => {
  const [reportOpen, setReportOpen] = useState(false);
  const [historyKey, setHistoryKey] = useState(0);
  const bumpHistory = () => setHistoryKey((k) => k + 1);
  return (
    <Layout>
      <div className="p-6 space-y-6" data-testid="hies-page">
        <div className="flex items-start gap-3">
          <div className="w-10 h-10 rounded-lg bg-emerald-100 flex items-center justify-center">
            <ShieldCheck className="w-5 h-5 text-emerald-600" />
          </div>
          <div className="flex-1">
            <h1 className="text-2xl font-bold text-slate-900">HIES</h1>
            <p className="text-sm text-slate-500">Deposit protection & Insurance Backed Guarantee collation, auto-updated as jobs move through the pipeline.</p>
          </div>
          <Button variant="outline" onClick={() => setReportOpen(true)} data-testid="hies-create-report-btn">
            <FilePlus className="w-4 h-4 mr-2" /> Create report
          </Button>
        </div>

        <SendHistory refreshKey={historyKey} />

        <Tabs defaultValue="deposit" className="w-full">
          <TabsList data-testid="hies-tabs">
            {TABLES.map((t) => (
              <TabsTrigger key={t.key} value={t.key} data-testid={`hies-tab-${t.key}`}>{t.label}</TabsTrigger>
            ))}
            <TabsTrigger value="settings" data-testid="hies-tab-settings">
              <FileSpreadsheet className="w-4 h-4 mr-1" /> Settings
            </TabsTrigger>
          </TabsList>
          {TABLES.map((t) => (
            <TabsContent key={t.key} value={t.key} className="mt-4">
              <HiesTable table={t.key} onSent={bumpHistory} />
            </TabsContent>
          ))}
          <TabsContent value="settings" className="mt-4">
            <HiesSettings />
          </TabsContent>
        </Tabs>

        <CustomReportDialog open={reportOpen} onOpenChange={setReportOpen} onSent={bumpHistory} />
      </div>
    </Layout>
  );
};

export default AdminHies;
